import _ from 'lodash'
import classnames from 'classnames'
import Modal from '../../../common/Modal'
import { useDispatch, useSelector } from 'react-redux'
import { useCallback, useEffect } from 'react'
import { toast } from 'react-toastify'
import { useTranslation } from 'react-i18next'

import { categoryCompare } from '../../../../utility/programHelper'
import { DUAL_PANEL_SELECTABLE_TYPES, PER_PAGE } from '../../../../constants/dual-panel-selector-constants'
import {
  addSelectedProgram,
  addSelectedStudent, deleteSelectedProgram,
  deleteSelectedStudent, resetSelectedData,
  selectDroppedStudentIds,
  selectEnrolledStudents,
  selectSelectedPrograms,
  selectSelectedStudents,
  selectWaitlistedStudents,
  updateSelectedStudent,
} from '../../../../reducers/programDashboardReducer'
import { reset, selectProgramsData, selectStudentsData, setSearchValue, setUnavailableStudents } from '../../../../reducers/dualPanelSelectorReducer'
import { getProgramsDualPanelSelectorThunk } from '../../../../actions/programActions'
import { getStudentsDualPanelSelector } from '../../../../actions/usersActions'
import DualPanelSelector from '../../../common/dual-panel-selector/DualPanelSelector'

import classes from './AddStudentsModal.module.scss'
import { ReactComponent as FlagIcon } from '../../../../assets/images/common/icons/Flag.svg'
import SubmitButton from '../../../common/buttons/SubmitButton'
import PropTypes from 'prop-types'

const SummaryPlaceholder = () => {
  const { t } = useTranslation()

  return (
    <div className={classes.placeholder}>
      <FlagIcon className={classes.icon} />
      <h2 className={classes.title}>{t('programs.manage.addStudentsModal.noStudentsAdded')}</h2>
      <p className={classes.description}>
        {t('programs.manage.addStudentsModal.placeholderDescription')}
      </p>
    </div>
  )
}

const AddStudentsModal = ({ title, onProceed, toWaitlist = false, ...props }) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()

  const communityId = useSelector(state => state.user.current_user.communities[0].id)
  const enrolledStudents = useSelector(selectEnrolledStudents)
  const droppedStudentIds = useSelector(selectDroppedStudentIds)
  const waitlistedStudents = useSelector(selectWaitlistedStudents)
  const studentsData = useSelector(selectStudentsData)
  const programsData = useSelector(selectProgramsData)
  const selectedStudentsMap = useSelector(state => state.programDashboard.addStudentsModal.selectedStudents.entities)
  const selectedStudents = useSelector(selectSelectedStudents)
  const selectedPrograms = useSelector(selectSelectedPrograms)

  const deleteStudent = (std, course = null) => {
    if (course) {
      const student = selectedStudentsMap[std.id]

      if (!student) {
        return
      }

      const from = { ...student.addedFrom }
      delete from[course.id]

      if (_.isEmpty(from)) {
        dispatch(deleteSelectedStudent(student.id))
      } else {
        dispatch(updateSelectedStudent({ ...student, addedFrom: from }))
      }
    } else {
      dispatch(deleteSelectedStudent(std.id))
    }
  }

  const addStudent = (std, course = null) => {
    if (!selectedStudentsMap[std.id]) {
      if (course) {
        dispatch(addSelectedStudent({ ...std, addedFrom: { [course.id]: course.name }, type: DUAL_PANEL_SELECTABLE_TYPES.students }))
      } else {
        dispatch(addSelectedStudent({ ...std, addedFrom: { students: true }, type: DUAL_PANEL_SELECTABLE_TYPES.students }))
      }
    }
  }

  const handleCheckStudent = (checked, student) => {
    if (checked) {
      deleteStudent(student)
    } else {
      addStudent(student)
    }
  }

  const handleCheckProgram = (checked, course) => {
    if (checked) {
      course.enrollments.map((e) => {
        deleteStudent(e.user, course)
      })
      dispatch(deleteSelectedProgram(course.id))
    } else {
      dispatch(addSelectedProgram(course))
      course.enrollments.map((e) => {
        if (!studentsData.unavailableItems.entities[e.userId] && e.user.status !== 'invited') {
          addStudent(e.user, course)
        }
      })
    }
  }

  const fetchPrograms = useCallback((page, searchTerm, completed) =>
    dispatch(getProgramsDualPanelSelectorThunk({ page, perPage: PER_PAGE, searchTerm, completed }))
      .unwrap()
      .catch(() => toast.error(t('common.somethingWentWrong'), { position: toast.POSITION.TOP_RIGHT }))
  , [dispatch, t])

  const fetchStudents = useCallback((page, searchTerm) =>
    dispatch(getStudentsDualPanelSelector({ page, perPage: PER_PAGE, communityId, searchTerm }))
      .unwrap()
      .catch(() => toast.error(t('common.somethingWentWrong'), { position: toast.POSITION.TOP_RIGHT }))
  , [communityId, dispatch, t])

  useEffect(() => {
    dispatch(resetSelectedData())
    // Dropped students are treated as already in the program, so they can't be re-added here.
    const droppedStudents = droppedStudentIds.map(id => ({ id }))
    const unavailableStudents = toWaitlist
      ? [...(enrolledStudents ?? []), ...(waitlistedStudents ?? []), ...droppedStudents]
      : [...(enrolledStudents ?? []), ...droppedStudents]
    dispatch(setUnavailableStudents(unavailableStudents))
    return () => dispatch(reset())
  }, [dispatch, enrolledStudents, droppedStudentIds, waitlistedStudents, toWaitlist])

  const leftPanelProps = {
    tabTypes: [DUAL_PANEL_SELECTABLE_TYPES.students, DUAL_PANEL_SELECTABLE_TYPES.programs],
    tabProps: {
      [DUAL_PANEL_SELECTABLE_TYPES.students]: {
        titleText: t('programs.manage.addStudentsModal.selectStudents', { count: selectedStudents.length }),
        allItems: studentsData.items,
        searchValue: studentsData.searchValue,
        onSearchValueChange: (searchValue) => dispatch(setSearchValue({ searchValue, key: DUAL_PANEL_SELECTABLE_TYPES.students })),
        getSelectedItem: (id) => selectedStudentsMap[id],
        fetchItems: fetchStudents,
        pagination: studentsData.pagination,
        onCheckChange: handleCheckStudent,
        isLoading: studentsData.isLoading,
      },
      [DUAL_PANEL_SELECTABLE_TYPES.programs]: {
        titleText: t('programs.manage.addStudentsModal.selectPrograms', { count: selectedPrograms.length }),
        allItems: programsData.items,
        withCompletedSwitcher: true,
        searchValue: programsData.searchValue,
        onSearchValueChange: (searchValue) => dispatch(setSearchValue({ searchValue, key: DUAL_PANEL_SELECTABLE_TYPES.programs })),
        getSelectedItem: (id) => selectedPrograms.find(program => program.id === id),
        fetchItems: fetchPrograms,
        pagination: programsData.pagination,
        onCheckChange: handleCheckProgram,
        itemCategoryTitle: (program) => program.category ? program.category.name : t('programs.common.otherPrograms'),
        itemCategoryCompare: categoryCompare,
        isLoading: programsData.isLoading,
      },
    },
  }

  const rightPanelProps = {
    selectedItems: selectedStudents,
    selectedItemType: DUAL_PANEL_SELECTABLE_TYPES.students,
    summaryPlaceholder: <SummaryPlaceholder />,
    onRemove: (student) => handleCheckStudent(true, student),
  }

  return (
    <Modal className={classes.modal} title={title} {...props}>
      <div className={classnames('d-flex flex-column', classes.modalContentWrapper)}>
        <DualPanelSelector
          type='ProgramRoster'
          className={classes.dualPanelSelector}
          {...leftPanelProps}
          {...rightPanelProps}
        />
        <div className='mt-3 d-flex justify-content-end'>
          <SubmitButton
            disabled={!selectedStudents.length}
            className={classes.button}
            buttonMessage={t('programs.common.addStudents')}
            onClick={() => {
              props.onClose()
              onProceed()
            }}
          />
        </div>
      </div>
    </Modal>
  )
}

AddStudentsModal.propTypes = {
  title: PropTypes.string.isRequired,
  onClose: PropTypes.func.isRequired,
  onProceed: PropTypes.func.isRequired,
  toWaitlist: PropTypes.bool,
}

export default AddStudentsModal
